import { atom } from 'jotai';

/**
 * Atom to control the visibility of the User Documents modal
 */
export const userDocumentsModalOpenAtom = atom<boolean>(false);

/**
 * State for the document preview modal.
 * Used to show a full-screen preview of uploaded documents.
 */
export interface DocumentPreviewState {
  /** Whether the preview modal is open */
  isOpen: boolean;
  /** The ID of the document being previewed (null when closed) */
  documentId: string | null;
}

/**
 * Atom to control the document preview modal state.
 * Set documentId to open the preview, set isOpen to false to close.
 */
export const documentPreviewAtom = atom<DocumentPreviewState>({
  isOpen: false,
  documentId: null,
});

/**
 * State for the artefact preview modal.
 * Used to preview generated artefacts (PDFs, patient handouts) inline
 * rather than opening in a new tab.
 */
export interface ArtefactPreviewState {
  /** Whether the preview modal is open */
  isOpen: boolean;
  /** Direct URL to the artefact file */
  url: string | null;
  /** Filename used to determine preview type (e.g. "report.pdf") */
  filename: string | null;
  /** Display title for the modal header */
  title: string | null;
  /**
   * Optional password to auto-unlock the document for the in-app preview.
   *
   * Currently used by the patient-summary delivery flow: the PDF served by
   * the proxy URL is encrypted with the patient's DOB so the patient must
   * enter it themselves when they open the SMS link, but the clinician
   * inside the app already knows the DOB and shouldn't be prompted — so we
   * pass it straight through to PDF.js.
   *
   * Has no effect for non-PDF artefacts.
   */
  password?: string | null;
  /**
   * Bytes already loaded for this preview. The preview creates and revokes
   * the object URL. `url` stays a remote address (document export, library
   * download) and is null when these bytes are the only source.
   */
  fileBlob?: Blob | null;
}

/**
 * Atom to control the artefact preview modal state.
 * Set url + filename to open, set isOpen to false to close.
 */
export const artefactPreviewAtom = atom<ArtefactPreviewState>({
  isOpen: false,
  url: null,
  filename: null,
  title: null,
  password: null,
});
